import { useCallback, useState } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { useFloating } from '@floating-ui/react';
import type { ConferenceTag } from '@trinity/chat/types';
import { PopperMenu, PopperMenuItem } from '../Popper/PopperMenu';
import { Portal } from '../Portal/index.js';
import { TagBadge } from './TagBadge';

type Props = {
  onDelete: () => void;
  onEdit: () => void;
  tag: Pick<ConferenceTag, 'color' | 'typeId' | 'name'>;
  className?: string;
};

export const TagBadgeMenu = ({ onDelete, onEdit, tag, className }: Props) => {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-start',
  });

  const handleClose = useCallback(() => setOpen(false), []);
  const handleOpen = useCallback(() => setOpen(true), []);

  const handleEdit = useCallback(() => {
    onEdit();
    setOpen(false);
  }, [onEdit]);

  const handleDelete = useCallback(() => {
    onDelete();
    setOpen(false);
  }, [onDelete]);

  return (
    <>
      <div ref={refs.setReference} onClick={handleOpen}>
        <TagBadge className={className} tag={tag} />
      </div>
      {open && (
        <Portal>
          <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleClose} touchEvent="onTouchStart">
            <div ref={refs.setFloating} style={floatingStyles}>
              <PopperMenu>
                <PopperMenuItem onClick={handleEdit}>Edit</PopperMenuItem>
                <PopperMenuItem onClick={handleDelete}>Delete</PopperMenuItem>
              </PopperMenu>
            </div>
          </ClickAwayListener>
        </Portal>
      )}
    </>
  );
};

TagBadgeMenu.displayName = 'ProjectClips.TagBadgeMenu';
